import {
  Required,
  IsString,
  DataValidator,
  transformKeys,
  IsNumber,
  IsBoolean,
  ValidateNested,
} from "@/utils/type-vaildator";
import COS from "cos-js-sdk-v5";
import api from "./api";

/** COS 临时凭证场景 */
export type CosTokenScene = "actor" | "voice" | "material" | "knowledge" | "common";

// 原始接口
export interface CosTokenCredentialsResponse {
  session_token: string;
  tmp_secret_id: string;
  tmp_secret_key: string;
}

export interface CosTokenResponse {
  expired_time: number;
  expiration: string;
  credentials: CosTokenCredentialsResponse;
  request_id: string;
  start_time: number;
  domain: string;
  bucket: string;
  region: string;
  prefix: string;
  scope_limit: boolean;
}

export class CosCredentialsData {
  @Required("sessionToken cannot be empty")
  @IsString("sessionToken must be a string")
  sessionToken: string;

  @Required("tmpSecretId cannot be empty")
  @IsString("tmpSecretId must be a string")
  tmpSecretId: string;

  @Required("tmpSecretKey cannot be empty")
  @IsString("tmpSecretKey must be a string")
  tmpSecretKey: string;
}

export class CosTokenData {
  @Required("expiredTime cannot be empty")
  @IsNumber("expiredTime must be a number")
  expiredTime: number;

  @Required("expiration cannot be empty")
  @IsString("expiration must be a string")
  expiration: string;

  @Required("credentials cannot be empty")
  @ValidateNested(CosCredentialsData)
  credentials: CosCredentialsData;

  @Required("requestId cannot be empty")
  @IsString("requestId must be a string")
  requestId: string;

  @Required("startTime cannot be empty")
  @IsNumber("startTime must be a number")
  startTime: number;

  @Required("domain cannot be empty")
  @IsString("domain must be a string")
  domain: string;

  @Required("bucket cannot be empty")
  @IsString("bucket must be a string")
  bucket: string;

  @Required("region cannot be empty")
  @IsString("region must be a string")
  region: string;

  @Required("prefix cannot be empty")
  @IsString("prefix must be a string")
  prefix: string;

  @Required("scopeLimit cannot be empty")
  @IsBoolean("scopeLimit must be a boolean")
  scopeLimit: boolean;
}

const transformAndValidateCosToken = (
  response: CosTokenResponse
): CosTokenData => {
  const transformedData = transformKeys(response);

  try {
    return DataValidator.validateOrThrow(transformedData, CosTokenData);
  } catch (error) {
    console.error("COS token data validation failed:", error.message);
    throw error;
  }
};

/**
 * 获取 COS 临时凭证（GET /cos/token）
 * @param scene actor：数字人；voice：音频；materia：素材库；knowledge：知识库
 */
export const getCosToken = async ({
  scene,
  signal,
}: {
  scene: CosTokenScene;
  signal?: AbortSignal;
}): Promise<CosTokenData> => {
  const response = await api.get<CosTokenResponse>("/cos/token", {
    scene,
    signal,
  });
  return transformAndValidateCosToken(response.data);
};

// 在服务端下发的 `prefix` 下生成唯一对象键
export function buildCosObjectKey(prefix: string, file: File): string {
  const trimmed = prefix.replace(/^\/+|\/+$/g, "");
  const lastDot = file.name.lastIndexOf(".");
  const ext =
    lastDot > 0
      ? file.name.slice(lastDot).replace(/[^\w.]/g, "")
      : "";
  const unique = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  return `${trimmed}/${unique}${ext}`;
}


// 去除 COS 的 ETag 引号
function stripCosEtagQuotes(etag: string): string {
  return etag.replace(/^"|"$/g, "");
}

// 用临时凭证构造 COS 客户端（STS）
export function createCosClientFromToken(token: CosTokenData): COS {
  const { credentials } = token;
  return new COS({
    SecretId: credentials.tmpSecretId,
    SecretKey: credentials.tmpSecretKey,
    SecurityToken: credentials.sessionToken,
    StartTime: token.startTime,
    ExpiredTime: token.expiredTime,
  });
}

export interface PutObjectWithCosTokenParams {
  file: File;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

export interface PutObjectWithCosTokenResult {
  key: string;
  url: string;
  etag: string;
  location: string;
}

/**
 * 使用已获取的临时凭证，在 token.prefix 下 putObject 并拼出可访问 url
 */
export async function putObjectWithCosToken(
  token: CosTokenData,
  { file, onProgress, signal }: PutObjectWithCosTokenParams
): Promise<PutObjectWithCosTokenResult> {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const cos = createCosClientFromToken(token);
  const key = buildCosObjectKey(token.prefix, file);
  const domainBase = token.domain.replace(/\/$/, "");

  let taskId: COS.TaskId | undefined;
  const onAbort = () => {
    if (taskId !== undefined) {
      try {
        cos.cancelTask(taskId);
      } catch {
        // ignore
      }
    }
  };
  if (signal) {
    signal.addEventListener("abort", onAbort, { once: true });
  }

  let result: COS.PutObjectResult;
  try {
    result = await cos.putObject({
      Bucket: token.bucket,
      Region: token.region,
      Key: key,
      Body: file,
      ContentType: file.type || "application/octet-stream",
      onTaskReady: (id) => {
        taskId = id;
      },
      onProgress: onProgress
        ? (info) => {
          onProgress(Math.min(100, Math.round(info.percent * 100)));
        }
        : undefined,
    });
  } finally {
    if (signal) {
      signal.removeEventListener("abort", onAbort);
    }
  }

  const data: PutObjectWithCosTokenResult = {
    key,
    url: `${domainBase}/${key}`,
    etag: stripCosEtagQuotes(result.ETag),
    location: result.Location,
  };

  console.log('cos upload data: ', { data });

  return data;
}
